<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { apiFetch } from '@/utils/api'

interface SessionCard {
  sessionId?: string | null
  userId?: string | null
  startTime?: string | null
  durationMs?: number | null
  eventCount?: number | null
  errorCount?: number | null
  hasErrors?: boolean | null
  deviceType?: string | null
  country?: string | null
  pageUrl?: string | null
  [key: string]: unknown
}

interface ForensicTimelineItem {
  when_utc?: string | null
  actor?: string | null
  action?: string | null
  endpoint?: string | null
  evidence?: string | null
}

interface UserMessage {
  role: 'user'
  content: string
  createdAt: string
}

interface AssistantMessage {
  role: 'assistant'
  answer: string
  citations: string[]
  sessions: SessionCard[]
  rqlExecuted: string
  forensicTimeline: ForensicTimelineItem[]
  warnings: string[]
  confidence: 'high' | 'low'
  sessionInScope?: string | null
  rounds?: number
  toolCalls?: number
  latencyMs?: number
  costCapReached?: boolean
  model?: string
  deep?: boolean
  promptTokens?: number
  outputTokens?: number
  createdAt: string
}

type Message = UserMessage | AssistantMessage

const props = defineProps<{
  projectId: string
  sessionId?: string | null
  embedded?: boolean
}>()

const emit = defineEmits<{
  (e: 'open-session', sessionId: string): void
}>()

const router = useRouter()

const question = ref('')
const loading = ref(false)
const errorMsg = ref<string | null>(null)
const messages = ref<Message[]>([])
const showRql = ref<Record<string, boolean>>({})

const DEEP_STORAGE_KEY = 'tracium.assistant.deep'
const deepMode = ref<boolean>(loadDeepPreference())

function loadDeepPreference(): boolean {
  try {
    const stored = localStorage.getItem(DEEP_STORAGE_KEY)
    if (stored === null) return true // default ON for new users
    return stored === 'true'
  } catch {
    return true
  }
}

function toggleDeepMode() {
  deepMode.value = !deepMode.value
  try { localStorage.setItem(DEEP_STORAGE_KEY, String(deepMode.value)) } catch { /* ignore */ }
}

const threadEl = ref<HTMLElement | null>(null)
const inputEl = ref<HTMLInputElement | null>(null)

const sessionInScope = computed(() => (props.sessionId ?? '').trim() || null)

const MODEL_PRICING: Record<string, { input: number; output: number }> = {
  'gpt-4.1':      { input: 2.00, output: 8.00 },
  'gpt-4o':       { input: 2.50, output: 10.00 },
  'gpt-4o-mini':  { input: 0.15, output: 0.60 },
  'gpt-4.1-mini': { input: 0.40, output: 1.60 },
  'gpt-4.1-nano': { input: 0.10, output: 0.40 },
  'o4-mini':      { input: 1.10, output: 4.40 },
  'o3-mini':      { input: 1.10, output: 4.40 },
  'o3':           { input: 2.00, output: 8.00 },
}
const DEFAULT_PRICING = MODEL_PRICING['gpt-4.1']

function estimateCostUsd(model: string | undefined, inTokens: number, outTokens: number): number {
  const p = (model && MODEL_PRICING[model]) || DEFAULT_PRICING
  return (inTokens / 1_000_000) * p.input + (outTokens / 1_000_000) * p.output
}

const tokenStats = computed(() => {
  let inTokens = 0
  let outTokens = 0
  let costUsd = 0
  let lastIn = 0
  let lastOut = 0
  let lastCost = 0
  let lastModel: string | undefined
  let assistantTurns = 0
  for (const m of messages.value) {
    if (m.role !== 'assistant') continue
    const mi = m.promptTokens ?? 0
    const mo = m.outputTokens ?? 0
    inTokens += mi
    outTokens += mo
    const c = estimateCostUsd(m.model, mi, mo)
    costUsd += c
    lastIn = mi; lastOut = mo; lastCost = c; lastModel = m.model
    assistantTurns++
  }
  return {
    inTokens,
    outTokens,
    totalTokens: inTokens + outTokens,
    costUsd,
    lastIn,
    lastOut,
    lastTotal: lastIn + lastOut,
    lastCost,
    lastModel,
    assistantTurns,
  }
})

function formatTokens(n: number): string {
  if (n < 1000) return String(n)
  if (n < 100_000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
  return Math.round(n / 1000) + 'k'
}

function formatUsd(n: number): string {
  if (n === 0) return '$0'
  if (n < 0.01) return '<$0.01'
  if (n < 1)   return '$' + n.toFixed(3)
  return '$' + n.toFixed(2)
}

const placeholderExamples = computed(() => sessionInScope.value
  ? [
      'why did the user get stuck here?',
      'what backend transactions ran for this session?',
      'find other sessions with the same error',
    ]
  : [
      'sessions with errors in the last 24 hours',
      'which sessions hit a 500 on checkout today?',
      'who triggered the failed POST /api/orders?',
    ])

async function scrollToBottom() {
  await nextTick()
  if (threadEl.value) {
    threadEl.value.scrollTop = threadEl.value.scrollHeight
  }
}

async function runAssistant() {
  const q = question.value.trim()
  if (!q || loading.value) return

  errorMsg.value = null
  loading.value = true

  messages.value.push({
    role: 'user',
    content: q,
    createdAt: new Date().toISOString(),
  })
  question.value = ''
  await scrollToBottom()

  try {
    const resp = await apiFetch(`/projects/${encodeURIComponent(props.projectId)}/assistant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question: q,
        sessionId: sessionInScope.value,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        deep: deepMode.value,
      })
    })
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }))
      errorMsg.value = body.error || `HTTP ${resp.status}`
      return
    }
    const data = await resp.json()
    messages.value.push({
      role: 'assistant',
      answer: data.answer || '',
      citations: Array.isArray(data.citations) ? data.citations : [],
      sessions: Array.isArray(data.sessions) ? data.sessions : [],
      rqlExecuted: data.rqlExecuted || '',
      forensicTimeline: Array.isArray(data.forensicTimeline) ? data.forensicTimeline : [],
      warnings: Array.isArray(data.warnings) ? data.warnings : [],
      confidence: data.confidence || 'low',
      sessionInScope: data.sessionInScope,
      rounds: data.rounds,
      toolCalls: data.toolCalls,
      latencyMs: data.latencyMs,
      costCapReached: data.costCapReached,
      model: data.model,
      deep: data.deep,
      promptTokens: data.usage?.promptTokens,
      outputTokens: data.usage?.outputTokens,
      createdAt: new Date().toISOString(),
    })
    await scrollToBottom()
  } catch (e) {
    errorMsg.value = e instanceof Error ? e.message : String(e)
  } finally {
    loading.value = false
    inputEl.value?.focus()
  }
}

function clearThread() {
  messages.value = []
  errorMsg.value = null
}

function openSession(sid: string | null | undefined) {
  if (!sid) return
  emit('open-session', sid)
  router.push({ name: 'session-replay', params: { id: sid } })
}

function formatDuration(ms?: number | null): string {
  if (!ms || ms < 0) return '—'
  if (ms < 1000) return `${ms} ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60_000).toFixed(1)} min`
}

function formatRelative(ts?: string | null): string {
  if (!ts) return ''
  const t = new Date(ts).getTime()
  const diff = Date.now() - t
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`
  return `${Math.round(diff / 86_400_000)}d ago`
}

function deviceIcon(d?: string | null): string {
  switch (d) {
    case 'desktop': return '🖥️'
    case 'mobile': return '📱'
    case 'tablet': return '📋'
    case 'bot': return '🤖'
    default: return ''
  }
}

function countryFlag(cc?: string | null): string {
  if (!cc || cc.length !== 2) return ''
  return cc.toUpperCase().replace(/./g, c =>
    String.fromCodePoint(127397 + c.charCodeAt(0)))
}

defineExpose({ clearThread })

onMounted(() => {
  inputEl.value?.focus()
})
</script>

<template>
  <div class="flex flex-col w-full h-full bg-base-100">
    <div class="flex items-center gap-2 px-4 py-2 border-b border-base-300 shrink-0">
      <div class="relative shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary shadow-sm">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary-content" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11 3l1.06 3.5L15.5 7.5 12.06 8.5 11 12l-1.06-3.5L6.5 7.5 9.94 6.5L11 3z"/>
          <path d="M18 13l.6 1.7L20.3 15.3l-1.7.6L18 17.6l-.6-1.7L15.7 15.3l1.7-.6L18 13z"/>
          <path d="M5 14l.5 1.5L7 16l-1.5.5L5 18l-.5-1.5L3 16l1.5-.5L5 14z"/>
        </svg>
      </div>
      <div class="flex-1 min-w-0">
        <div class="text-sm font-semibold">Tracium Assistant</div>
      </div>
      <button
        v-if="messages.length > 0"
        type="button"
        class="btn btn-ghost btn-xs gap-1"
        style="margin-left: 50px; margin-right: 15px;"
        @click="clearThread"
        data-testid="assistant-clear"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 6h18"/>
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
          <path d="M10 11v6"/>
          <path d="M14 11v6"/>
          <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
        </svg>
        <span>Clear</span>
      </button>
    </div>

    <div
      ref="threadEl"
      class="flex-1 overflow-y-auto px-4 py-6 space-y-4 bg-base-200"
      data-testid="assistant-thread"
    >
      <div v-if="messages.length === 0" class="flex flex-col items-center justify-center h-full text-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-14 w-14 mb-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9.94 6.5L11 3l1.06 3.5L15.5 7.5 12.06 8.5 11 12l-1.06-3.5L6.5 7.5 9.94 6.5z"/>
          <path d="M17 14l.5 1.5L19 16l-1.5.5L17 18l-.5-1.5L15 16l1.5-.5L17 14z"/>
          <path d="M5 16l.5 1.5L7 18l-1.5.5L5 20l-.5-1.5L3 18l1.5-.5L5 16z"/>
        </svg>
        <div class="text-base font-medium text-base-content/70 mb-6">
          Ask anything about your sessions
        </div>
        <div class="flex flex-col gap-2 items-stretch w-full max-w-md">
          <button
            v-for="(ex, i) in placeholderExamples"
            :key="i"
            type="button"
            class="btn btn-sm btn-outline btn-block justify-start text-left normal-case font-normal gap-2"
            @click="question = ex; inputEl?.focus()"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 opacity-60 shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11 3l1.06 3.5L15.5 7.5 12.06 8.5 11 12l-1.06-3.5L6.5 7.5 9.94 6.5L11 3z"/>
            </svg>
            <span class="flex-1">{{ ex }}</span>
          </button>
        </div>
      </div>

      <template v-for="msg in messages" :key="msg.createdAt">
        <div v-if="msg.role === 'user'" class="chat chat-end">
          <div class="chat-bubble chat-bubble-primary whitespace-pre-wrap">{{ msg.content }}</div>
          <div class="chat-footer opacity-50 text-xs mt-1">{{ formatRelative(msg.createdAt) }}</div>
        </div>

        <div v-else class="chat chat-start">
          <div class="chat-header text-xs opacity-70 mb-1 flex items-center gap-2 flex-wrap">
            <span>🤖 Assistant</span>
            <span
              v-if="msg.deep !== undefined"
              class="badge badge-xs gap-1"
              :class="msg.deep ? 'badge-primary' : 'badge-ghost'"
              :title="msg.deep ? 'Answered with reasoning model' : 'Answered with fast model'"
            >{{ msg.deep ? '🧠 Deep' : '⚡ Fast' }}</span>
            <span
              class="badge badge-xs"
              :class="msg.confidence === 'high' ? 'badge-success' : 'badge-warning'"
            >{{ msg.confidence }}</span>
            <span v-if="msg.costCapReached" class="badge badge-warning badge-xs">cost cap</span>
            <span class="opacity-50">
              {{ msg.rounds }}r · {{ msg.toolCalls }}t
              <span v-if="msg.latencyMs">· {{ Math.round((msg.latencyMs ?? 0) / 100) / 10 }}s</span>
            </span>
          </div>

          <div class="chat-bubble bg-base-100 text-base-content shadow-sm max-w-2xl">
            <div class="text-sm whitespace-pre-wrap">{{ msg.answer }}</div>

            <div v-if="msg.citations.length > 0" class="mt-3 flex flex-col gap-1">
              <div
                v-for="(c, j) in msg.citations"
                :key="j"
                class="text-xs font-mono bg-base-200 rounded px-2 py-1 break-all whitespace-pre-wrap leading-relaxed"
              >{{ c }}</div>
            </div>

            <div v-if="msg.forensicTimeline.length > 0" class="mt-3 overflow-x-auto">
              <table class="table table-xs bg-base-200 rounded">
                <thead>
                  <tr>
                    <th>When (UTC)</th>
                    <th>Who</th>
                    <th>What</th>
                    <th>Endpoint</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in msg.forensicTimeline" :key="idx">
                    <td class="font-mono whitespace-nowrap">{{ item.when_utc || '—' }}</td>
                    <td>{{ item.actor || '—' }}</td>
                    <td>{{ item.action || '—' }}</td>
                    <td class="font-mono">{{ item.endpoint || '—' }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="mt-2 flex flex-col gap-1">
                <div
                  v-for="(item, idx) in msg.forensicTimeline.filter(x => x.evidence)"
                  :key="idx"
                  class="text-xs font-mono bg-base-200 rounded px-2 py-1 break-all whitespace-pre-wrap leading-relaxed"
                >{{ item.evidence }}</div>
              </div>
            </div>

            <div v-if="msg.warnings.length > 0" class="mt-3 flex flex-col gap-1">
              <div
                v-for="(w, j) in msg.warnings"
                :key="j"
                class="alert alert-warning py-1 px-2 text-xs"
              >{{ w }}</div>
            </div>

            <div v-if="msg.rqlExecuted" class="mt-3">
              <button
                type="button"
                class="btn btn-ghost btn-xs gap-1"
                @click="showRql[msg.createdAt] = !showRql[msg.createdAt]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="16 18 22 12 16 6"/>
                  <polyline points="8 6 2 12 8 18"/>
                </svg>
                <span>{{ showRql[msg.createdAt] ? 'Hide' : 'Show' }} executed RQL</span>
              </button>
              <pre v-if="showRql[msg.createdAt]" class="mt-2 text-xs bg-base-200 rounded p-3 overflow-x-auto"><code>{{ msg.rqlExecuted }}</code></pre>
            </div>

            <div v-if="msg.sessions.length > 0" class="mt-3 space-y-2">
              <div class="text-xs text-base-content/50">{{ msg.sessions.length }} session(s)</div>
              <button
                v-for="(s, k) in msg.sessions"
                :key="s.sessionId || k"
                type="button"
                class="w-full text-left bg-base-200 hover:bg-base-300 border border-base-300 rounded-lg p-2 transition-colors"
                data-testid="assistant-card"
                @click="openSession(s.sessionId)"
              >
                <div class="flex items-center gap-2 text-xs text-base-content/60">
                  <span v-if="s.deviceType" class="text-base">{{ deviceIcon(s.deviceType) }}</span>
                  <span v-if="s.country" class="text-base">{{ countryFlag(s.country) }}</span>
                  <span v-if="s.userId" class="font-mono">{{ s.userId }}</span>
                  <span v-if="s.startTime" class="ml-auto">{{ formatRelative(s.startTime) }}</span>
                </div>
                <div class="text-sm truncate" :title="s.pageUrl ?? ''">{{ s.pageUrl || '—' }}</div>
                <div class="flex items-center gap-2 mt-0.5 text-xs text-base-content/70">
                  <span v-if="s.eventCount">{{ s.eventCount }} events</span>
                  <span v-if="s.durationMs">· {{ formatDuration(s.durationMs) }}</span>
                  <span v-if="s.errorCount && s.errorCount > 0" class="badge badge-error badge-xs ml-auto">
                    ERR {{ s.errorCount }}
                  </span>
                  <span v-else-if="s.hasErrors" class="badge badge-error badge-xs ml-auto">errors</span>
                </div>
              </button>
            </div>
          </div>
          <div class="chat-footer opacity-50 text-xs mt-1">{{ formatRelative(msg.createdAt) }}</div>
        </div>
      </template>

      <div v-if="loading" class="chat chat-start" data-testid="assistant-typing">
        <div class="chat-header text-xs opacity-70 mb-1">🤖 Assistant</div>
        <div class="chat-bubble bg-base-100 text-base-content/70 shadow-sm">
          <div class="flex items-center gap-2">
            <span class="loading loading-dots loading-sm"></span>
            <span class="text-xs italic">thinking… (5–15s)</span>
          </div>
        </div>
      </div>
    </div>

    <div class="border-t border-base-300 bg-base-100 p-3 shrink-0">
      <div v-if="errorMsg" class="alert alert-error mb-2 text-sm py-2">{{ errorMsg }}</div>

      <div
        v-if="messages.length > 0"
        class="flex items-center justify-between gap-2 mb-2 text-xs text-base-content/60 font-mono"
        data-testid="assistant-token-counter"
        :title="`Thread totals across ${tokenStats.assistantTurns} assistant ${tokenStats.assistantTurns === 1 ? 'reply' : 'replies'}. Cost is an estimate using each reply's model (falls back to gpt-4.1 prices when unknown). Token caps reset every 60s on Tier 1 (gpt-4.1: 30k/min).`"
      >
        <div class="flex items-center gap-3 flex-wrap">
          <span class="opacity-70">tokens</span>
          <span>
            <span class="text-base-content/90">{{ formatTokens(tokenStats.inTokens) }}</span>
            <span class="opacity-50"> in</span>
          </span>
          <span>
            <span class="text-base-content/90">{{ formatTokens(tokenStats.outTokens) }}</span>
            <span class="opacity-50"> out</span>
          </span>
          <span>
            <span class="text-base-content/90">{{ formatTokens(tokenStats.totalTokens) }}</span>
            <span class="opacity-50"> total</span>
          </span>
          <span class="opacity-50">·</span>
          <span class="text-base-content/90">{{ formatUsd(tokenStats.costUsd) }}</span>
        </div>
        <div class="flex items-center gap-2 opacity-60">
          <span v-if="tokenStats.lastModel" class="badge badge-ghost badge-xs font-mono">{{ tokenStats.lastModel }}</span>
          <span v-if="tokenStats.assistantTurns > 0">
            last: {{ formatTokens(tokenStats.lastTotal) }} ({{ formatUsd(tokenStats.lastCost) }})
          </span>
          <span v-else class="italic">no reply yet</span>
        </div>
      </div>

      <form @submit.prevent="runAssistant" class="flex gap-2">
        <button
          type="button"
          class="btn btn-sm gap-1"
          :class="deepMode ? 'btn-primary' : 'btn-ghost border border-base-300'"
          :disabled="loading"
          data-testid="assistant-deep-toggle"
          :title="deepMode
            ? 'Deep thinking ON — uses a reasoning model (slower, better at multi-step questions). Click to switch to fast mode.'
            : 'Fast mode — uses gpt-4.1, no reasoning. Click to enable deep thinking.'"
          @click="toggleDeepMode"
        >
          <span class="text-base leading-none">{{ deepMode ? '🧠' : '⚡' }}</span>
          <span class="text-xs font-semibold">{{ deepMode ? 'Deep' : 'Fast' }}</span>
        </button>
        <input
          ref="inputEl"
          v-model="question"
          type="text"
          class="input input-bordered flex-1"
          :placeholder="placeholderExamples[0]"
          maxlength="1000"
          :disabled="loading"
          data-testid="assistant-input"
          @keydown.enter.prevent="runAssistant"
        />
        <button
          type="submit"
          class="btn btn-primary px-3"
          :disabled="loading || !question.trim() || !projectId"
          data-testid="assistant-submit"
          :title="loading ? 'Thinking…' : 'Send'"
          aria-label="Send"
        >
          <span v-if="loading" class="loading loading-spinner loading-xs"></span>
          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11 3l1.06 3.5L15.5 7.5 12.06 8.5 11 12l-1.06-3.5L6.5 7.5 9.94 6.5L11 3z"/>
            <path d="M18 13l.6 1.7L20.3 15.3l-1.7.6L18 17.6l-.6-1.7L15.7 15.3l1.7-.6L18 13z"/>
            <path d="M5 14l.5 1.5L7 16l-1.5.5L5 18l-.5-1.5L3 16l1.5-.5L5 14z"/>
          </svg>
        </button>
      </form>
    </div>
  </div>
</template>
